import { Box } from "@mui/system";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import StatusBreakdownCard from "./AnalyticsTasksStatus";
import AnalyticsBarCard from "./AnalyticsBarCard";
import FilterPill from "../Common/FilterPill";
import { getUserAttributes } from "../../utils/auth";
import {
  ActionItemGroupByKey,
  capitalizeFirstLetter,
  isValidDate,
} from "../../utils/utils";
import {
  ChartDataItem,
  DateRangeValue,
  SCORE_API_TO_UI_MAP,
  SCORE_OPTIONS,
  SCORE_UI_TO_API_MAP,
  SelectedChartBar,
  UserTeamData,
} from "../../utils/types";
import DataTable from "../Common/Datatable";
import getColumns from "./CallQualityInteractionTableConfig";
import AnalyticalAnalysisService from "../../services/analytics/AnalyticsService";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { GlobalContext } from "../../context/GlobalContext";
import ColumnFilter from "../Common/customFilter";

interface InteractionsAnalyticsProps {
  selectedTab: "my-analytics" | "team-analytics" | "account-analytics";
  analyticsDateRange: DateRangeValue;
  newUserTeamData: UserTeamData | null;
  setAnalyticsDateRange: React.Dispatch<React.SetStateAction<DateRangeValue>>;
  interactionType: string;
}

type SectionDefinition = {
  key: string;
  label: string;
};

const FALLBACK_SECTIONS: SectionDefinition[] = [
  { key: "opening", label: "Call Opening" },
  { key: "execution", label: "Call Execution" },
  { key: "closing", label: "Call Closing" },
];
const PRIORITY_SECTION_KEYS = ["opening", "execution", "closing"];

const normalizeSectionKey = (key: string): string =>
  String(key || "")
    .trim()
    .replace(/^call_/i, "")
    .replace(/\s+/g, "_")
    .toLowerCase();

const getSectionLabel = (normalizedKey: string): string => {
  const fallbackLabel = FALLBACK_SECTIONS.find(
    (section) => section.key === normalizedKey
  )?.label;

  if (fallbackLabel) {
    return fallbackLabel;
  }

  return String(normalizedKey)
    .split("_")
    .map((word) => capitalizeFirstLetter(word))
    .join(" ");
};

const getSectionFromAnalyst = (analyst: any, normalizedKey: string) => {
  if (!analyst) return null;

  if (analyst[normalizedKey]) return analyst[normalizedKey];
  if (analyst[`call_${normalizedKey}`]) return analyst[`call_${normalizedKey}`];

  const match = Object.entries(analyst).find(([rawKey]) => {
    return normalizeSectionKey(rawKey) === normalizedKey;
  });

  return match?.[1] || null;
};

const buildSectionDefinitions = (
  apiCategories: string[] = [],
): SectionDefinition[] => {
  const mergedSections: SectionDefinition[] = [];
  const seen = new Set<string>();

  const addSection = (rawKey: string) => {
    const normalizedKey = normalizeSectionKey(rawKey);
    if (!normalizedKey || seen.has(normalizedKey)) return;

    seen.add(normalizedKey);
    mergedSections.push({
      key: normalizedKey,
      label: getSectionLabel(normalizedKey),
    });
  };

  apiCategories.forEach(addSection);
  if (apiCategories.length === 0) {
    FALLBACK_SECTIONS.forEach((section) => addSection(section.key));
  }

  return mergedSections.sort((a, b) => {
    const aPriority = PRIORITY_SECTION_KEYS.indexOf(a.key);
    const bPriority = PRIORITY_SECTION_KEYS.indexOf(b.key);

    const aRank = aPriority === -1 ? Number.MAX_SAFE_INTEGER : aPriority;
    const bRank = bPriority === -1 ? Number.MAX_SAFE_INTEGER : bPriority;

    if (aRank !== bRank) return aRank - bRank;
    return 0;
  });
};

const generateRows = (apiData: any[] = [], sectionDefinitions: SectionDefinition[] = []) => {
  return apiData.map((item) => {
    const analyst = item?.abstractiveSummary?.analyst;

    const mapAnswers = (list: any[]) =>
      list.map((q) => capitalizeFirstLetter(q.answer || ""));

    const dynamicSectionData = sectionDefinitions.reduce((acc, section) => {
      const sectionValue = getSectionFromAnalyst(analyst, section.key);
      const sectionQuestions = sectionValue?.questions || [];

      acc[`section_summary_${section.key}`] = sectionValue?.score || "-";
      acc[`section_answers_${section.key}`] = mapAnswers(sectionQuestions);
      return acc;
    }, {} as Record<string, any>);

    return {
      id: item.interactionId,
      title: item.title || "-",
      qualityScore: item.score,
      startTime: item.startTime || "",
      endTime: item.endTime || "",
      ...dynamicSectionData,
    };
  });
};

const InteractionsAnalytics = ({
  selectedTab,
  analyticsDateRange,
  newUserTeamData,
  interactionType,
}: InteractionsAnalyticsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { accountDetails } = useContext(GlobalContext);
  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
  const savedInteractionsState = location.state?.interactionsState;
  const UserInfo = getUserAttributes();
  const loggedInEmail = UserInfo?.email;
  const isSeventhGearUser = loggedInEmail?.endsWith("@7thgear.ai");
  const isSellcordUser = loggedInEmail?.endsWith("@sellcord.co");
  const [expandedSection, setExpandedSection] = useState<string | null>(
    savedInteractionsState?.expandedSection ?? null
  );
  const [pinnedColumns] = useState({
    left: ["title"],
  });
  const MAX_ACCOUNT_BARS = 10;
  const [pageNumber, setPageNumber] = useState(savedInteractionsState?.pageNumber ?? 1);
  const [pageSize, setPageSize] = useState(savedInteractionsState?.pageSize ?? 50);
  const [totalRecord, setTotalRecord] = useState(0);
  const [rows, setRows] = useState<any[]>([]);
  const { showSnackbar } = useDisplaySnackbar();
  const [sectionDefinitions, setSectionDefinitions] = useState<SectionDefinition[]>(
    FALLBACK_SECTIONS
  );
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    title: true,
    score: true,
    section_summary_opening: true,
    section_summary_execution: true,
    section_summarny_closig: true,
    ...(savedInteractionsState?.columnVisibility ?? {}),
  });
  const [questionSchema, setQuestionSchema] = useState<Record<string, string[]>>(
    FALLBACK_SECTIONS.reduce((acc, section) => {
      acc[section.key] = [];
      return acc;
    }, {} as Record<string, string[]>)
  );
  const [groupBy, setGroupBy] = useState<ActionItemGroupByKey>(
    savedInteractionsState?.groupBy ?? ActionItemGroupByKey.CLIENT
  );
  const [scoreByList, setScoreByList] = useState<
    { status: string; totalCount: number }[]
  >([
    { status: "red", totalCount: 0 },
    { status: "yellow", totalCount: 0 },
    { status: "green", totalCount: 0 },
    { status: "not_rated", totalCount: 0 },
  ]);
  const [selectedChartBar, setSelectedChartBar] = useState<SelectedChartBar | null>(
    savedInteractionsState?.selectedChartBar ?? null
  );
  const [accountChartData, setAccountChartData] = useState<ChartDataItem[]>([]);
  const [arrChartData, setArrChartData] = useState<ChartDataItem[]>([]);
  const [organizerChartData, setOrganizerChartData] = useState<ChartDataItem[]>([]);
  const [accountFilterOptions, setAccountFilterOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const graphPageLimit = 10;
  const [graphOffset, setGraphOffset] = useState(1);
  const [graphTotalRecords, setGraphTotalRecords] = useState(0);

  const totalInteractionCount = useMemo(() => {
    return scoreByList.reduce((sum, item) => sum + (item?.totalCount ?? 0), 0);
  }, [scoreByList]);

  const scoreOptionsForUser = useMemo(() => {
    if (!isSellcordUser) return SCORE_OPTIONS;

    return SCORE_OPTIONS.map((option) => {
      if (option.key === "red") return { ...option, label: "Immediate" };
      if (option.key === "yellow") return { ...option, label: "Close Watch" };
      if (option.key === "green") return { ...option, label: "Positive" };
      return option;
    });
  }, [isSellcordUser]);

  const [selectedScore, setSelectedScore] = useState<{
    key: string;
    label: string;
    count: number;
  }>(
    savedInteractionsState?.selectedScore ?? {
      key: "total",
      label: "Total",
      count: totalInteractionCount,
    }
  );
  const skipInitialTabReset = useRef(Boolean(savedInteractionsState));

  const employeePorId =
    selectedTab === "team-analytics"
      ? newUserTeamData?.currentUser
      : loggedInProfileOrgId;
  const shouldCallApi =
    !!employeePorId &&
    isValidDate(analyticsDateRange?.startDate) &&
    isValidDate(analyticsDateRange?.endDate);

  const isOrganizerOthers =
    selectedChartBar?.groupBy === ActionItemGroupByKey.ORGANIZER &&
    selectedChartBar?.key === "__organizer_others__";

  const hasTeamForSelectedUser = (newUserTeamData?.subordinates?.length ?? 0) > 0;
  const isForTeamAnalytics = selectedTab === "team-analytics" && hasTeamForSelectedUser;
  const {
    isLoading: isInteractionsLoading,
    isSuccess: isInteractionsSuccess,
    isFetching: isInteractionsFetching,
    isError: isInteractionsError,
    error: interactionsError,
    data: interactionsData,
  } = AnalyticalAnalysisService.useGetInteractionsCallList(
    {
      employeePorId: employeePorId || loggedInProfileOrgId,
      isForTeam: isForTeamAnalytics,
      isForAccount: selectedTab === "account-analytics",
      startTime: analyticsDateRange.startDate || "",
      endTime: analyticsDateRange.endDate || "",
      offset: pageNumber,
      limit: pageSize,
      score:
        selectedScore.key === "total"
          ? "all"
          : SCORE_UI_TO_API_MAP[selectedScore.key],
      organizerPorId: selectedChartBar?.groupBy === ActionItemGroupByKey.ORGANIZER && !isOrganizerOthers
        ? selectedChartBar.key
        : "",
      isOtherInteractionsFilter: isOrganizerOthers,
      clientOrgId: selectedChartBar?.groupBy === ActionItemGroupByKey.CLIENT
        ? selectedChartBar.key
        : "",
        arrCategory: selectedChartBar?.groupBy === ActionItemGroupByKey.ARR
        ? selectedChartBar.key.toUpperCase()
        : "",
      interactionTypeFilter: interactionType,
    },
    shouldCallApi
  );

  const {
    isLoading: isLoadingScores,
    isSuccess: isSuccessScores,
    isFetching: isFetchingScores,
    isError: isErrorScores,
    error: scoresError,
    data: scoresData,
  } = AnalyticalAnalysisService.useGetInteractionsCallScore(
    {
      employeePorId: employeePorId || loggedInProfileOrgId,
      isForTeam: isForTeamAnalytics,
      startTime: analyticsDateRange.startDate || "",
      endTime: analyticsDateRange.endDate || "",
      isForAccount: selectedTab === "account-analytics",
      interactionTypeFilter: interactionType,
    },
    shouldCallApi
  );

  const {
    isLoading: isLoadingQualityGraph,
    isSuccess: isSuccessQualityGraph,
    isFetching: isFetchingQualityGraph,
    isError: isErrorQualityGraph,
    error: qualityGraphError,
    data: qualityGraphData,
  } = AnalyticalAnalysisService.useGetCallQualityGraph(
    {
      employeePorId: employeePorId || loggedInProfileOrgId,
      isForTeam: isForTeamAnalytics,
      isForAccount: selectedTab === "account-analytics",
      startTime: analyticsDateRange.startDate || "",
      endTime: analyticsDateRange.endDate || "",
      score:
        selectedScore.key === "total"
          ? "all"
          : SCORE_UI_TO_API_MAP[selectedScore.key],
      callQualityGraphView:
        groupBy === ActionItemGroupByKey.CLIENT
          ? "client"
          : groupBy === ActionItemGroupByKey.ORGANIZER
            ? "organizer"
            : "arr",
      ...(groupBy === ActionItemGroupByKey.CLIENT && { 
        limit: graphPageLimit, 
        offset: graphOffset
      }),
      interactionTypeFilter: interactionType,
    },
    shouldCallApi
  );

  const {
    isLoading: isLoadingAccountsFilter,
    isSuccess: isSuccessAccountsFilter,
    isFetching: isFetchingAccountsFilter,
    isError: isErrorAccountsFilter,
    error: accountsFilterError,
    data: accountsFilterData,
  } = AnalyticalAnalysisService.useGetAccountsFilterList({
    employeePorId: employeePorId || loggedInProfileOrgId,
    isForTeam: isForTeamAnalytics,
    startTime: analyticsDateRange.startDate || "",
    endTime: analyticsDateRange.endDate || "",
    interactionTypeFilter: interactionType,
    isForAccount: selectedTab === "account-analytics",
    callQualityGraphView:
    groupBy === ActionItemGroupByKey.CLIENT
      ? "client"
      : groupBy === ActionItemGroupByKey.ORGANIZER
        ? "organizer"
        : "arr",
    score:
    selectedScore.key === "total"
      ? "all"
      : SCORE_UI_TO_API_MAP[selectedScore.key],

  }, shouldCallApi);

  useEffect(() => {
    if (isSuccessAccountsFilter && accountsFilterData) {
      const responseList = Array.isArray(
        accountsFilterData?.data?.clientInteractionsCount
      )
        ? accountsFilterData.data.clientInteractionsCount
        : [];

      setAccountFilterOptions(
        responseList.map((item: any) => ({
          label:
            capitalizeFirstLetter(item?.clientName) ||
            item?.clientDomain ||
            "Unknown",
          value:
            item?.clientOrgId || item?.clientDomain || item?.clientName || "",
        }))
      );
      return;
    }

    if (isErrorAccountsFilter) {
      showSnackbar("error", "Error fetching accounts filter data");
    }
  }, [
    isLoadingAccountsFilter,
    isSuccessAccountsFilter,
    isFetchingAccountsFilter,
    isErrorAccountsFilter,
    accountsFilterError,
    accountsFilterData,
  ]);

  useEffect(() => {
    setGraphOffset(1);
  }, [
    groupBy,
    selectedScore.key,
    employeePorId,
    analyticsDateRange.startDate,
    analyticsDateRange.endDate,
    selectedTab,
  ]);

  useEffect(() => {
    if (isInteractionsSuccess && interactionsData) {
      const fetchedData = interactionsData?.data?.data || [];
      const responseCategories = interactionsData?.data?.categories || [];
      setTotalRecord(interactionsData?.data?.totalRecords || 0);

      const discoveredSections: SectionDefinition[] = [];
      const discoveredQuestions: Record<string, string[]> = {};

      fetchedData.forEach((row: any) => {
        const analyst = row?.abstractiveSummary?.analyst;
        if (!analyst || typeof analyst !== "object") return;

        Object.entries(analyst).forEach(([rawKey, rawSection]) => {
          if (!rawSection || typeof rawSection !== "object") return;

          const sectionObj = rawSection as { questions?: any[] };
          const hasSectionShape =
            "score" in sectionObj || Array.isArray(sectionObj.questions);
          if (!hasSectionShape) return;

          const normalizedKey = normalizeSectionKey(rawKey);
          if (!normalizedKey) return;

          if (!discoveredSections.some((section) => section.key === normalizedKey)) {
            discoveredSections.push({
              key: normalizedKey,
              label: getSectionLabel(normalizedKey),
            });
          }

          const questions = Array.isArray(sectionObj.questions)
            ? sectionObj.questions
            : [];

          if (!discoveredQuestions[normalizedKey]) {
            discoveredQuestions[normalizedKey] = [];
          }

          questions.forEach((q: any) => {
            const questionText = String(q?.question || "").trim();
            if (
              questionText &&
              !discoveredQuestions[normalizedKey].includes(questionText)
            ) {
              discoveredQuestions[normalizedKey].push(questionText);
            }
          });
        });
      });

      const finalSections = buildSectionDefinitions(
        responseCategories,
      );
      const finalQuestionSchema = finalSections.reduce((acc, section) => {
        acc[section.key] = discoveredQuestions[section.key] || [];
        return acc;
      }, {} as Record<string, string[]>);

      setSectionDefinitions(finalSections);
      setQuestionSchema(finalQuestionSchema);
      setRows(generateRows(fetchedData, finalSections));
    } else if (isInteractionsError) {
      showSnackbar("error", "Error fetching interactions data");
    }
  }, [
    isInteractionsLoading,
    isInteractionsFetching,
    isInteractionsError,
    interactionsError,
    interactionsData,
    isInteractionsSuccess,
  ]);

  useEffect(() => {
    if (isSuccessScores) {
      const apiList = scoresData?.data || [];
      // init all UI scores with 0
      const uiScoreMap: Record<string, number> = {
        red: 0,
        yellow: 0,
        green: 0,
        not_rated: 0,
      };

      apiList?.forEach((item: any) => {
        const apiScore: string = item?.score; // already normalized
        const count: number = item?.count ?? 0;

        const uiKey = SCORE_API_TO_UI_MAP[apiScore];

        if (uiKey) {
          uiScoreMap[uiKey] += count; // safe even if multiple map to same bucket
        }
      });

      const mappedList = [
        { status: "red", totalCount: uiScoreMap.red },
        { status: "yellow", totalCount: uiScoreMap.yellow },
        { status: "green", totalCount: uiScoreMap.green },
        { status: "not_rated", totalCount: uiScoreMap.not_rated },
      ];

      setScoreByList(mappedList);
    } else if (isErrorScores) {
      showSnackbar("error", "Error fetching interactions scores");
    }
  }, [
    isSuccessScores,
    scoresData,
    isErrorScores,
    scoresError,
    isLoadingScores,
    isFetchingScores,
  ]);

  useEffect(() => {
    if (isSuccessQualityGraph) {
      const apiData = qualityGraphData.data;
      setGraphTotalRecords(apiData?.totalRecords ?? 0);
    /* ---------- ACCOUNT / CLIENT ---------- */
    const accountData: ChartDataItem[] =
      (apiData.clientInteractionsCount || []).map((item: any) => ({
        key: item.clientOrgId,
        label: capitalizeFirstLetter(item.clientName) || item.clientDomain || "Unknown",
        value: item.totalInteractions || 0,
        status: selectedScore.label,
      }));

    /* ---------- ORGANIZER ---------- */
    const totalOthersCount = (apiData.organizerInteractionsCount || []).reduce(
      (sum: number, item: any) => sum + (item.otherInteractionCount || 0),
      0
    );
    let organizerData: ChartDataItem[] =
    (apiData.organizerInteractionsCount || []).map((item: any) => {
      const fullName =
        item.organizerFullName?.trim() ||
        item.organizerEmail ||
        "Unknown";
  
      const firstName = fullName.includes("@")
        ? fullName.split("@")[0]               // email fallback
        : fullName.split(/\s+/)[0];            // first name only
  
      return {
        key: item.organizerPorId,
        label: firstName,                      // 👈 X-axis label
        fullLabel: fullName,                   // 👈 hover / tooltip
        value: item.totalOrganizerInteractions || 0,
        status: selectedScore.label,
      };
    });
    organizerData = organizerData.filter(item => item.value > 0);
    if (totalOthersCount > 0) {
      organizerData.push({
        key: "__organizer_others__", // IMPORTANT: not "others"
        label: "Others",
        value: totalOthersCount,
        status: selectedScore.label,
      });
    }
      /* ---------- ARR (High / Medium / Low) ---------- */
    const arrDefaults = ["high", "medium", "low"];

    const arrMap: Record<string, number> = {
      high: 0,
      medium: 0,
      low: 0,
    };
    let hasNoArr = false;

    (apiData.arrInteractionsCount || []).forEach((item: any) => {
      const key = item.arrCategory?.toLowerCase();
    
      if (!key) return;
    
      if (key === "no_arr") {
        hasNoArr = true;
        arrMap.no_arr = item.totalInteractions || 0; // add dynamically
        return;
      }
    
      if (arrMap[key] !== undefined) {
        arrMap[key] = item.totalInteractions || 0;
      }
    });
    
    // build chart data
    const arrData: ChartDataItem[] = [
      ...arrDefaults.map((k) => ({
        key: k,
        label: capitalizeFirstLetter(k),
        value: arrMap[k],
        status: selectedScore.label,
      })),
    
      ...(hasNoArr
        ? [
            {
              key: "no_arr",
              label: "No ARR",
              value: arrMap.no_arr || 0,
              status: selectedScore.label,
            },
          ]
        : []),
    ];

    setAccountChartData(accountData);
    setOrganizerChartData(organizerData);
    setArrChartData(arrData);
    } else if (isErrorQualityGraph) {
      showSnackbar(
        "error", "Error fetching call quality graph data"
      );
    }
  }, [
    isSuccessQualityGraph,
    qualityGraphData,
    isErrorQualityGraph,
    qualityGraphError,
    isLoadingQualityGraph,
    isFetchingQualityGraph,
    groupBy,
  ]);
  // Get the color for the selected score
  const getSelectedScoreColor = () => {
    const scoreOption = scoreOptionsForUser.find(
      (opt) => opt.key === selectedScore.key
    );
    return scoreOption?.color;
  };

  const scoreCountMap = useMemo(() => {
    const map: Record<string, number> = {};
    scoreByList.forEach((s) => {
      map[s.status] = s.totalCount;
    });
    return map;
  }, [scoreByList]);
  // Filter options for the filter pills
  const scoreFilterOptions = useMemo(() => {
    return scoreOptionsForUser
      .filter((opt) => opt.key !== "total")
      .map((opt) => ({
      label: `${opt.label} (${scoreCountMap[opt.key] ?? 0})`,
      value: opt.key,
      labelColour: opt.color,
    }));
  }, [scoreCountMap, scoreOptionsForUser]);

  const chartBarPills = useMemo(() => {
      if (!selectedChartBar) return [];
    
      // ❗ Do NOT show bar pill for Account group (handled by Account filter pill)
      if (selectedChartBar.groupBy === ActionItemGroupByKey.CLIENT) return [];
    
      return [
        {
          key:
            selectedChartBar.groupBy === ActionItemGroupByKey.ORGANIZER
              ? "organizer"
              : "arr",
          value: selectedChartBar.label,
        },
      ];
    }, [selectedChartBar]);

  const columnPickerOptions = useMemo(() => {
    const baseOptions = [
      { value: "title", label: "Interaction Title", defaultValue: true },
      { value: "score", label: "Rating", defaultValue: true },
    ];

    const sectionOptions = sectionDefinitions.map((section) => ({
      value: `section_summary_${section.key}`,
      label: section.label,
    }));

    return [...baseOptions, ...sectionOptions];
  }, [sectionDefinitions]);

  useEffect(() => {
    setColumnVisibility((prev) => {
      const next = { ...prev };

      sectionDefinitions.forEach((section) => {
        const key = `section_summary_${section.key}`;
        if (next[key] === undefined) {
          next[key] = true;
        }
      });

      return next;
    });
  }, [sectionDefinitions]);

  const rawChartData: ChartDataItem[] = useMemo(() => {
    if (groupBy === ActionItemGroupByKey.CLIENT) {
      return accountChartData;
    }
  
    if (groupBy === ActionItemGroupByKey.ORGANIZER) {
      return organizerChartData;
    }
  
    if (groupBy === ActionItemGroupByKey.ARR) {
      return arrChartData;
    }
    return [];
  }, [groupBy, accountChartData, organizerChartData, arrChartData]);
  

  const isAccountGroup = groupBy === ActionItemGroupByKey.CLIENT;

  const chartData: ChartDataItem[] = useMemo(() => {
    const shouldShowAccountDots = isAccountGroup && graphTotalRecords > MAX_ACCOUNT_BARS;
    const sorted = shouldShowAccountDots
      ? [...rawChartData].sort((a, b) => b.value - a.value)
      : rawChartData;
    const othersValue = shouldShowAccountDots
      ? sorted
          .slice(MAX_ACCOUNT_BARS)
          .reduce((sum, d) => sum + d.value, 0)
      : 0;

    if (
      shouldShowAccountDots &&
      selectedChartBar?.groupBy === ActionItemGroupByKey.CLIENT &&
      selectedChartBar.key
    ) {
      const matchedAccount = rawChartData.find(
        (item) => item.key === selectedChartBar.key
      );

      // Account found in current page — normal view
      if (matchedAccount) {
        return [
          matchedAccount,
          {
            key: "others",
            label: "Other Accounts",
            value: othersValue,
            status: selectedScore.label,
            isDots: true,
          },
        ];
      }

      // Account is in "Others" (not in current page). 
      // using the label from accountFilterOptions so the 2-bar view still shows.
      const fallbackLabel =
        accountFilterOptions.find((o) => o.value === selectedChartBar.key)?.label ||
        selectedChartBar.label ||
        "Selected Account";

      return [
        {
          key: selectedChartBar.key,
          label: fallbackLabel,
          value: selectedChartBar.value ?? 0,
          status: selectedScore.label,
        },
        {
          key: "others",
          label: "Other Accounts",
          value: othersValue,
          status: selectedScore.label,
          isDots: true,
        },
      ];
    }

    if (!isAccountGroup) {
      return rawChartData;
    }

    if (!shouldShowAccountDots) {
      return rawChartData;
    }
  
    const topTen = sorted.slice(0, MAX_ACCOUNT_BARS);

    return [
      ...topTen,
      {
        key: "others",
        label: "Other Accounts",
        value: othersValue,
        status: selectedScore.label,
        isDots: true,
      },
    ];
  }, [rawChartData, isAccountGroup, selectedScore, selectedChartBar, graphTotalRecords]);
  
  const getScoreCount = (scoreKey: string) => {
    if (scoreKey === "total") return totalInteractionCount;

    const scoreItem = scoreByList.find((item) => item.status === scoreKey);

    return scoreItem?.totalCount ?? 0;
  };

  useEffect(() => {
    if (!selectedScore?.key) return;

    const updatedCount = getScoreCount(selectedScore.key);

    setSelectedScore((prev) => {
      if (prev.count === updatedCount) return prev; // prevent loops

      return {
        ...prev,
        count: updatedCount,
      };
    });
  }, [scoreByList, totalInteractionCount]);

  useEffect(() => {
    if (selectedTab) {
      if (skipInitialTabReset.current) {
        skipInitialTabReset.current = false;
        return;
      }
      setSelectedScore({
        key: "total",
        label: "Total",
        count: totalInteractionCount,
      });
      setSelectedChartBar(null);
      setExpandedSection(null);
      setGroupBy(ActionItemGroupByKey.CLIENT);
    }
  }, [selectedTab]);

  useEffect(() => {
    if (selectedScore || newUserTeamData?.currentUser || groupBy) {
      setSelectedChartBar(null);
    }
  }, [selectedScore, newUserTeamData?.currentUser, groupBy]);

  const handleInteractionTitleClick = (row: any) => {
    if (!row?.id) return;

    navigate(`/meeting/${row.id}`, {
      state: {
        parent_page_identifier: "analytics-interactions-page",
        selectedTab,
        selectedAnalyticsTab: selectedTab,
        newUserTeamData,
        analyticsDateRange,
        interactionType,
        interactionsState: {
          pageNumber,
          pageSize,
          selectedScore,
          groupBy,
          selectedChartBar,
          columnVisibility,
          expandedSection,
        },
      },
    });
  };

  const filteredGroupByOptions = useMemo(() => {
    const options = [
      { label: "Account", value: ActionItemGroupByKey.CLIENT },
      { label: "ARR", value: ActionItemGroupByKey.ARR },
    ];
    // Do not show Organizer for Account Analytics
    if (selectedTab !== "account-analytics") {
      options.push({
        label: "Organizer",
        value: ActionItemGroupByKey.ORGANIZER,
      });
    }
    return options;
  }, [selectedTab]);

  const onPageChange = (value: number) => {
    setPageNumber(value);
  };
  const onPaginationChange = (pageNumber: number) => {
    onPageChange(pageNumber + 1);
  };

  const shouldShowOthersDots = isAccountGroup && graphTotalRecords > MAX_ACCOUNT_BARS;
  const selectedAccountValue =
  selectedChartBar?.groupBy === ActionItemGroupByKey.CLIENT
    ? selectedChartBar.key
    : null;
  const selectedAccountLabel =
    selectedChartBar?.groupBy === ActionItemGroupByKey.CLIENT
      ? selectedChartBar.label
      : null;
  const shouldLockGraphPagination =
    groupBy === ActionItemGroupByKey.CLIENT &&
    graphTotalRecords > MAX_ACCOUNT_BARS &&
    !!selectedAccountValue;

  return (
    <Box className="analytics-tasks-container">
      <Box className="analytics-tasks-main">
        <Box className="analytics-status">
          <StatusBreakdownCard
            statusCounts={scoreByList}
            onStatusClick={(payload) => {
              setSelectedScore({
                key: payload.key,
                label: payload.label,
                count: payload.count,
              });
            }}
            selectedStatus={selectedScore}
            loading={isLoadingScores || isFetchingScores}
            totalCount={totalInteractionCount}
            statusOptions={scoreOptionsForUser}
            filteredStatusOptions={scoreOptionsForUser}
            statusLabels={{
              statusText: "Scores",
              breakdownText: "No. of Interactions",
            }}
            showDropdown={false}
            iconType="square"
          />
        </Box>
        <Box className="analytics-tasks-bar-card" mt={0.5}>
          <AnalyticsBarCard
            title={selectedScore.label}
            totalCount={selectedScore.count}
            groupBy={groupBy}
            groupByOptions={filteredGroupByOptions}
            onGroupByChange={setGroupBy}
            chartData={chartData}
            loading={isLoadingQualityGraph || isFetchingQualityGraph}
            onBarClick={(item) => {
              setSelectedChartBar({
                key: item.key ?? item.label,
                label: item.label,
                value: item.value,
                groupBy,
              })}
            }
            selectedChartBar={selectedChartBar}
            isSeventhGearUser={isSeventhGearUser}
            showColorBox={true}
            selectedColor={getSelectedScoreColor()}
            selectedKey={selectedScore.key}
            labelSuffix="Interactions"
            enableOthersDots={shouldShowOthersDots}
            hoverText="Interactions"
            barChartHeight={250}
            showPagination={groupBy === ActionItemGroupByKey.CLIENT}
            offset={graphOffset}
            limit={graphPageLimit}
            totalRecords={graphTotalRecords}
            onOffsetChange={setGraphOffset}
            disablePaginationControls={shouldLockGraphPagination}
            accountFilterOptions={accountFilterOptions}
            selectedAccountValue={selectedAccountValue || undefined}
            onAccountFilterChange={(val) => {
              if (!val) {
                setSelectedChartBar(null);
                return;
              }
              const opt = accountFilterOptions.find((o) => o.value === val);
              setSelectedChartBar({
                key: String(val),
                label: opt?.label || "",
                value: 0,
                groupBy: ActionItemGroupByKey.CLIENT,
              });
            }}
          />
        </Box>
      </Box>
      <Box className="analytics-tasks-table">
        <Box className="table-header">
          <Box className="task-table-title">
            <h1 className="table-title-text">Interaction List</h1>
            <Box className="table-filters">
              <FilterPill
                label={"Score"}
                value={selectedScore.key === "total" ? "All Interactions" : selectedScore.key}
                options={scoreFilterOptions}
                displayValue={selectedScore.key === "total" ? "All" : undefined}
                displayAll={true}
                showClearIcon={selectedScore.key !== "total"}
                onChange={(val) => {
                  if (!val) {
                    setSelectedScore({
                      key: "total",
                      label: "Total",
                      count: totalInteractionCount,
                    });
                    return;
                  }

                  const opt = scoreOptionsForUser.find((o) => o.key === val);

                  setSelectedScore({
                    key: val as string,
                    label: opt?.label || String(val),
                    count: getScoreCount(val as string),
                  });
                }}
              />
              {groupBy === ActionItemGroupByKey.CLIENT && (
                <FilterPill
                  label="Account"
                  type="select"
                  options={accountFilterOptions}
                  value={selectedAccountValue || undefined}
                  displayValue={selectedAccountLabel || "All"}
                  displayAll
                  showClearIcon={!!selectedAccountValue}
                  enableAccountSearch={true}
                  onChange={(val) => {
                    if (!val) {
                      setSelectedChartBar(null);
                    } else {
                      const opt = accountFilterOptions.find((o) => o.value === val);

                      setSelectedChartBar({
                        key: String(val),           // clientOrgId
                        label: opt?.label || "",
                        value: 0,                   // not needed for pill
                        groupBy: ActionItemGroupByKey.CLIENT,
                      });
                    }
                  }}
                />
              )}

              {chartBarPills.map(({ key, value }) => (
                <FilterPill
                  key={key}
                  label={capitalizeFirstLetter(key)}
                  value={[value]}
                  displayValue={value}
                  readOnly
                  showClearIcon
                  onChange={() => setSelectedChartBar(null)}
                />
              ))}

            </Box>
          </Box>
          <Box className="table-actions">
            <ColumnFilter
              currentValue={columnVisibility}
              onApply={(val) => setColumnVisibility(val as Record<string, boolean>)}
              options={columnPickerOptions}
              type="toggle"
              showSections={{
                search: true,
                sort: false,
              }}
            />
          </Box>
        </Box>
        <Box className="table-content">
          {/* Placeholder table for interactions */}
          <Box className="interactions-table-container">
            <DataTable
              columns={getColumns(
                expandedSection,
                setExpandedSection,
                sectionDefinitions,
                questionSchema,
                columnVisibility,
                handleInteractionTitleClick
              )}
              rows={rows}
              rowCount={totalRecord}
              pageSize={pageSize}
              onPaginationChange={onPaginationChange}
              onSortingChange={() => {}}
              timeRange={""}
              calendarId={null}
              layoutMode="semantic"
              pinnedColumns={pinnedColumns}
              tablePadding={"0px 0px"}
              minHeight={"auto"}
              maxHeight={"400px"}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              showVerticalDividers={true}
              showSpinner={isInteractionsLoading || isInteractionsFetching}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InteractionsAnalytics;
